<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <div ref="container" class="screen__stretch pretty-scrollbar">
      <BaseForm
        ref="form"
        v-bind="form"
        class="screen__form"
        @submit="onFormSubmit"
      >
        <i18n
          path="texts.registrationTerms"
          tag="div"
          class="form__terms"
          slot="content"
        >
          <button
            type="button"
            class="form__terms-link"
            v-text="$t('texts.termsRegistrationConsent')"
            @click="showTermsModal"
          />
        </i18n>
      </BaseForm>
    </div>
    <BaseButton
      v-if="!isThanksVisible"
      :text="$t('titles.personal')"
      theme="secondary"
      slot="actions"
      @click="goToSignInScreen"
    />
    <template #root>
      <ModalRequirements
        v-if="isRequirementsModalVisible"
        :requirements="requirements"
        class="screen__overlay"
        @close="hideRequirementsModal"
      />
      <ModalTerms
        v-if="isTermsModalVisible"
        v-bind="termsModal"
        class="screen__overlay"
        @close="hideTermsModal"
      />
    </template>
  </BaseScreen>
</template>

<script>
import { mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import formMixin from '@/mixins/form';
import faceMixin from '@/mixins/face';

import FieldLogin from '@/components/FieldLogin.vue';
import FieldEmail from '@/components/FieldEmail.vue';
import FieldPhone from '@/components/FieldPhone.vue';
import BaseTooltip from '@/components/BaseTooltip.vue';
import BaseButton from '@/components/BaseButton.vue';
import ModalRequirements from '@/components/ModalRequirements.vue';
import ModalTerms from '@/components/ModalTerms.vue';

const FIELDS = Object.freeze({
  LOGIN: 'login',
  EMAIL: 'email',
  PHONE: 'phone',
});

export default {
  name: 'SignUpScreen',
  mixins: [screenMixin, formMixin, faceMixin],
  components: {
    BaseButton,
    ModalRequirements,
    ModalTerms,
  },
  data() {
    return {
      values: {
        [FIELDS.LOGIN]: '',
        [FIELDS.EMAIL]: '',
        [FIELDS.PHONE]: '+7',
      },
      isTermsShowed: false,
      isIntroReplicSayed: false,
      isTermsModalVisible: false,
      isRequirementsModalVisible: false,
      isLoginComplete: false,
      isEmailComplete: false,
      isPhoneComplete: false,
      isLoginError: false,
      isEmailError: false,
      isPhoneError: false,
      requirementsFieldName: null,
      errorMessage: '',
      invalidLogins: [],
      invalidEmails: [],
    };
  },
  staticData: () => ({
    replicInterval: null,
    focusTimeout: null,
  }),
  computed: {
    titleText() {
      return this.isThanksVisible ? '' : this.$t('titles.signup');
    },
    screenCase() {
      if (this.isPending) {
        return null;
      }
      if (!this.isIntroReplicSayed) {
        return CONSTANTS.STEP_SIGN_UP;
      }
      if (this.isThanksVisible) {
        return CONSTANTS.STEP_REGISTRATION_SUCCESS;
      }
      if (this.isErrorVisible) {
        return CONSTANTS.STEP_CONNECTION_ERROR;
      }
      if (this.errorMessage) {
        return CONSTANTS.STEP_FORM_ERROR;
      }
      if (this.isTermsModalVisible) {
        return CONSTANTS.STEP_TERMS_MODAL;
      }
      if (this.isRequirementsModalVisible) {
        if (this.requirementsFieldName === FIELDS.LOGIN) {
          return CONSTANTS.STEP_LOGIN_MODAL;
        }
        if (this.requirementsFieldName === FIELDS.EMAIL) {
          return CONSTANTS.STEP_EMAIL_MODAL;
        }
        if (this.requirementsFieldName === FIELDS.PHONE) {
          return CONSTANTS.STEP_PHONE_MODAL;
        }
      }
      if (this.isLoginError) {
        return CONSTANTS.STEP_LOGIN_ERROR;
      }
      if (!this.isLoginComplete) {
        return CONSTANTS.STEP_LOGIN;
      }
      if (this.isEmailError) {
        return CONSTANTS.STEP_EMAIL_ERROR;
      }
      if (!this.isEmailComplete) {
        return CONSTANTS.STEP_EMAIL;
      }
      if (this.isPhoneError) {
        return CONSTANTS.STEP_PHONE_ERROR;
      }
      if (!this.isPhoneComplete) {
        return CONSTANTS.STEP_PHONE;
      }
      if (this.isRegistrationReady) {
        return CONSTANTS.STEP_REGISTRATION_READY;
      }
      if (this.isFieldsComplete) {
        return CONSTANTS.STEP_FIELDS_READY;
      }
      return null;
    },
    isRegistrationReady() {
      return Boolean(this.isTermsShowed && this.isFieldsComplete);
    },
    isFieldsComplete() {
      return Boolean(this.isLoginComplete && this.isEmailComplete && this.isPhoneComplete);
    },
    isSubmitDisabled() {
      return !this.isFieldsComplete;
    },
    hasVisibleModal() {
      return this.isTermsModalVisible || this.isRequirementsModalVisible;
    },
    stepFieldName() {
      if (!this.isLoginComplete) return FIELDS.LOGIN;
      if (!this.isEmailComplete) return FIELDS.EMAIL;
      if (!this.isPhoneComplete) return FIELDS.PHONE;
      return null;
    },
    errorFieldName() {
      if (this.isLoginError) return FIELDS.LOGIN;
      if (this.isEmailError) return FIELDS.EMAIL;
      if (this.isPhoneError) return FIELDS.PHONE;
      return null;
    },
    requirements() {
      const list = {
        [FIELDS.LOGIN]: [
          this.$t('fields.loginLengthRequirement'),
          this.$t('fields.loginLetterRequirement'),
        ],
        [FIELDS.EMAIL]: [
          this.$t('fields.emailRequirement'),
        ],
        [FIELDS.PHONE]: [
          this.$t('fields.phoneRequirement'),
        ],
      };
      return list[this.requirementsFieldName] || [];
    },
    retryAction() {
      return this.onFormSubmit;
    },
    form() {
      return {
        values: this.values,
        fields: [
          {
            is: FieldLogin,
            name: FIELDS.LOGIN,
            invalidValues: this.invalidLogins,
            tooltip: {
              is: BaseTooltip,
              action: () => this.showRequirementsByName(FIELDS.LOGIN),
            },
            on: {
              blur: () => this.checkFieldCompleteByName(FIELDS.LOGIN),
            },
          },
          {
            is: FieldEmail,
            name: FIELDS.EMAIL,
            invalidValues: this.invalidEmails,
            tooltip: {
              is: BaseTooltip,
              action: () => this.showRequirementsByName(FIELDS.EMAIL),
            },
            on: {
              blur: () => this.checkFieldCompleteByName(FIELDS.EMAIL),
            },
          },
          {
            is: FieldPhone,
            name: FIELDS.PHONE,
            tooltip: {
              is: BaseTooltip,
              action: () => this.showRequirementsByName(FIELDS.PHONE),
            },
            on: {
              blur: () => this.checkFieldCompleteByName(FIELDS.PHONE),
            },
          },
        ],
        actions: [
          {
            type: 'submit',
            text: this.$t('actions.register'),
            disabled: this.isSubmitDisabled,
          },
        ],
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.thanksForRegistration'),
          size: 'l',
        },
        description: {
          text: `${this.$t('texts.confirmLinkWasSended')} <b>${this.values.email}</b>`,
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.enterPersonal'),
            action: this.goToSignInScreen,
          },
        ],
      };
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badConnection'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.badConnection'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryErrorAction,
          },
        ],
      };
    },
    termsModal() {
      return {
        type: 'registration',
        title: {
          text: this.$t('titles.personalTerms'),
          size: 'm',
          isNormal: true,
        },
      };
    },
  },
  watch: {
    values: {
      deep: true,
      handler() {
        this.setReplicInterval();
        this.clearErrorMessage();
      },
    },
    hasVisibleModal: 'onModalVisibleChanged',
    faces: 'onFacesCountChanged',
  },
  beforeDestroy() {
    this.clearReplicInterval();
    this.clearFocusTimeout();
  },
  created() {
    this.initActions([
      [CONSTANTS.ACTION_SHOW_MODAL, this.showTermsModal],
      [CONSTANTS.ACTION_PERSONAL, this.goToSignInScreen],
      [CONSTANTS.ACTION_CLOSE, this.hideVisibleModal],
    ]);
  },
  methods: {
    ...mapActions('personal', [
      'signup',
    ]),
    async onFormSubmit() {
      if (this.isPending) return;
      try {
        this.showPending();
        this.clearReplicInterval();
        this.clearFocusTimeout();
        await this.signup(this.values);
        this.showThanks();
      } catch (e) {
        const { message = '' } = e || {};
        const isNetworkError = message.toLowerCase() === 'network error';
        const isLoginError = message.toLowerCase() === 'имя пользователя занято.';
        const isEmailError = message.toLowerCase() === 'пользователь с таким e-mail уже существует.';

        // Ошибка логина
        if (isLoginError) {
          const name = FIELDS.LOGIN;
          const login = this.values[name];
          this.invalidLogins.push(login);
          this.$nextTick(() => {
            const field = this.$refs.form?.getFieldByName(name);
            field?.setErrorMessage();
            this.checkFieldCompleteByName(name);
            this.hidePending();
          });
          return;
        }

        // Ошибка почты
        if (isEmailError) {
          const name = FIELDS.EMAIL;
          const email = this.values[name];
          this.invalidEmails.push(email);
          this.$nextTick(() => {
            const field = this.$refs.form?.getFieldByName(name);
            field?.setErrorMessage();
            this.checkFieldCompleteByName(name);
            this.hidePending();
          });
          return;
        }

        // Остальные ошибки
        if (message && !isNetworkError) {
          this.errorMessage = message;
          this.sayText(message);
          this.$refs.form?.setMessage(message);
          this.$refs.container.scrollTop = 0;
          this.hidePending();
        } else {
          this.showError();
        }

        console.error('signup error: ', e);
      }
    },
    checkFieldCompleteByName(name) {
      if (!name) return;
      const field = this.$refs.form?.getFieldByName(name);
      const isValid = field?.checkValidity();
      const hasValue = field?.checkHasValue();
      const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
      this[`is${capitalizedName}Error`] = hasValue && !isValid;
      this[`is${capitalizedName}Complete`] = isValid;
      this.setFocusTimeout();
    },
    setFieldFocusOrScrollToButton() {
      if (this.isFieldsComplete) {
        this.$refs.container.scrollTop = 5000;
        return;
      }
      const name = this.errorFieldName || this.stepFieldName;
      this.$refs.form?.setFieldFocusByName(name);
    },
    setReplicInterval() {
      const interval = this.screenCase === CONSTANTS.STEP_FIELDS_READY ? 20000 : 10000;
      this.clearReplicInterval();
      this.replicInterval = window.setInterval(this.setScreenCase, interval);
    },
    setFocusTimeout() {
      this.focusTimeout = window.setTimeout(this.setFieldFocusOrScrollToButton, 250);
    },
    clearErrorMessage() {
      this.errorMessage = '';
    },
    clearReplicInterval() {
      window.clearInterval(this.replicInterval);
    },
    clearFocusTimeout() {
      window.clearTimeout(this.focusTimeout);
    },
    onModalVisibleChanged(hasModal) {
      return hasModal ? this.clearFocusTimeout() : this.setFocusTimeout();
    },
    onFacesCountChanged(faces) {
      if (faces.length === 0) {
        this.clearReplicInterval();
      }
    },
    onScreenCaseChanged(caseName) {
      this.setScreenCase();

      // Отслеживаем завершение вступительной реплики
      if (caseName === CONSTANTS.STEP_SIGN_UP) {
        return this.$root.$once(CONSTANTS.ROBOT_REPLIC_FINISH, () => {
          this.isIntroReplicSayed = true;
        });
      }

      const shouldIgnoreReplicInterval = !caseName || [
        CONSTANTS.STEP_LOGIN_MODAL,
        CONSTANTS.STEP_EMAIL_MODAL,
        CONSTANTS.STEP_PHONE_MODAL,
        CONSTANTS.STEP_TERMS_MODAL,
        CONSTANTS.STEP_REGISTRATION_SUCCESS,
        CONSTANTS.STEP_CONNECTION_ERROR,
      ].includes(caseName);

      return shouldIgnoreReplicInterval
        ? this.clearReplicInterval()
        : this.setReplicInterval();
    },
    showRequirementsByName(name) {
      this.requirementsFieldName = name;
      this.showRequirementsModal();
    },
    showTermsModal() {
      this.isTermsModalVisible = true;
      this.isTermsShowed = true;
    },
    hideTermsModal() {
      this.isTermsModalVisible = false;
    },
    showRequirementsModal() {
      this.isRequirementsModalVisible = true;
    },
    hideRequirementsModal() {
      this.isRequirementsModalVisible = false;
    },
    hideVisibleModal() {
      if (this.isTermsModalVisible) {
        this.hideTermsModal();
      }
      if (this.isRequirementsModalVisible) {
        this.hideRequirementsModal();
      }
    },
    goToSignInScreen() {
      this.$router.push(this.$router.options.paths.signIn);
    },
  },
};
</script>
